/**
 * Office Settings Card — for HR/Director to set office GPS coordinates & geofence radius.
 * Used by Self-Service Portal clock-in feature.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Edit2, Save, X, Loader, Crosshair } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OfficeSettingsCard = () => {
  const [config, setConfig] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ lat: '', lng: '', radius_meters: '', address: '' });

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/me/office-config`);
      setConfig(data);
      setForm({
        lat: data.lat,
        lng: data.lng,
        radius_meters: data.radius_meters,
        address: data.address || '',
      });
    } catch (e) {
      console.error('Failed to load office config', e);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Browser anda tidak menyokong GPS');
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((s) => ({
          ...s,
          lat: parseFloat(pos.coords.latitude.toFixed(6)),
          lng: parseFloat(pos.coords.longitude.toFixed(6)),
        }));
        toast.success(`GPS dikesan: ${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`);
        setBusy(false);
      },
      (err) => {
        toast.error(err.message || 'Gagal dapat lokasi GPS');
        setBusy(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        lat: parseFloat(form.lat),
        lng: parseFloat(form.lng),
        radius_meters: parseInt(form.radius_meters),
        address: form.address || '',
      };
      if (Number.isNaN(payload.lat) || Number.isNaN(payload.lng) || Number.isNaN(payload.radius_meters)) {
        toast.error('Sila isi koordinat & radius dengan nombor yang sah');
        setBusy(false);
        return;
      }
      const { data } = await axios.put(`${API}/admin/office-config`, payload);
      setConfig(data.config);
      setEditing(false);
      toast.success('Tetapan pejabat dikemas kini');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal simpan tetapan');
    } finally {
      setBusy(false);
    }
  };

  if (!config) return null;

  return (
    <div data-testid="office-settings-card" className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 sm:p-5 mb-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm sm:text-base font-bold text-gray-800">Tetapan Pejabat (GPS)</h3>
            <p className="text-[11px] sm:text-xs text-gray-500">Untuk geofence Clock-In/Out di Portal Self-Service</p>
          </div>
        </div>
        {!editing && (
          <button
            data-testid="edit-office-config-btn"
            onClick={() => setEditing(true)}
            className="px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-50 flex items-center gap-1 flex-shrink-0"
          >
            <Edit2 className="w-3.5 h-3.5" /> Edit
          </button>
        )}
      </div>

      {!editing ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs sm:text-sm">
          <div className="bg-white rounded-lg p-2.5 border border-gray-100">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Latitude</p>
            <p className="font-semibold text-gray-800 mt-0.5">{config.lat}</p>
          </div>
          <div className="bg-white rounded-lg p-2.5 border border-gray-100">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Longitude</p>
            <p className="font-semibold text-gray-800 mt-0.5">{config.lng}</p>
          </div>
          <div className="bg-white rounded-lg p-2.5 border border-gray-100">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Radius</p>
            <p className="font-semibold text-gray-800 mt-0.5">{config.radius_meters}m</p>
          </div>
          {config.address && (
            <div className="sm:col-span-3 bg-white rounded-lg p-2.5 border border-gray-100">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Alamat</p>
              <p className="font-medium text-gray-700 mt-0.5">{config.address}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-gray-500 font-medium block mb-1">Latitude</label>
              <input
                data-testid="office-lat"
                type="number"
                step="0.000001"
                value={form.lat}
                onChange={(e) => setForm((s) => ({ ...s, lat: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                placeholder="3.139000"
              />
            </div>
            <div>
              <label className="text-[11px] text-gray-500 font-medium block mb-1">Longitude</label>
              <input
                data-testid="office-lng"
                type="number"
                step="0.000001"
                value={form.lng}
                onChange={(e) => setForm((s) => ({ ...s, lng: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                placeholder="101.686900"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-gray-500 font-medium block mb-1">Radius (meter)</label>
            <input
              data-testid="office-radius"
              type="number"
              step="10"
              min="10"
              max="10000"
              value={form.radius_meters}
              onChange={(e) => setForm((s) => ({ ...s, radius_meters: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              placeholder="200"
            />
            <p className="text-[10px] text-gray-400 mt-1">Cadangan: 100-300m. Staff perlu berada dalam radius ini untuk clock-in.</p>
          </div>
          <div>
            <label className="text-[11px] text-gray-500 font-medium block mb-1">Alamat Pejabat</label>
            <input
              data-testid="office-address"
              type="text"
              value={form.address}
              onChange={(e) => setForm((s) => ({ ...s, address: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              placeholder="Underads Sdn Bhd, KL"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button
              data-testid="use-gps-btn"
              type="button"
              onClick={useCurrentLocation}
              disabled={busy}
              className="px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              <Crosshair className="w-4 h-4" /> Guna GPS Saya
            </button>
            <div className="flex gap-2 sm:ml-auto">
              <button
                data-testid="save-office-config-btn"
                type="button"
                onClick={save}
                disabled={busy}
                className="flex-1 sm:flex-none px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {busy ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Simpan
              </button>
              <button
                type="button"
                onClick={() => { setEditing(false); fetchConfig(); }}
                disabled={busy}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1.5"
              >
                <X className="w-4 h-4" /> Batal
              </button>
            </div>
          </div>
          <p className="text-[10px] text-gray-400 italic pt-1">
            Tip: Klik "Guna GPS Saya" sambil di pejabat, atau guna Google Maps untuk dapatkan koordinat tepat.
          </p>
        </div>
      )}
    </div>
  );
};

export default OfficeSettingsCard;
